import React, { useState, useEffect, useRef } from 'react';
import ReactPlayer from 'react-player';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import FlagIcon from '@mui/icons-material/Flag';
import GreenFlagWidget from './GreenFlagWidget';

/**
 * Props:
 *   videoData       - VideoData object or null
 *   playerRef       - callback ref attached to ReactPlayer
 *   playerRefs      - full refs array from useBroadcastSync (for polling currentTime)
 *   slotIndex       - index of this slot in playerRefs
 *   isPrimary       - large left slot
 *   isPlaying       - main timeline play state (paused when solo is active)
 *   playbackRate
 *   onPromote
 *   onGreenFlagSave
 *   needsGreenFlag  - auto-show widget if missing sync anchor
 *   onStartSolo     - tell BroadcastViewer to pause main timeline
 *   onEndSolo       - tell BroadcastViewer solo mode is done
 */
function BroadcastVideoSlot({
  videoData,
  playerRef,
  playerRefs,
  slotIndex,
  isPrimary = false,
  isPlaying = false,
  playbackRate = 1,
  onPromote,
  onGreenFlagSave,
  needsGreenFlag = false,
  onStartSolo,
  onEndSolo,
}) {
  const [showGreenFlag, setShowGreenFlag] = useState(false);
  const [soloIsPlaying, setSoloIsPlaying] = useState(false);
  const [soloCurrentTime, setSoloCurrentTime] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const pollRef = useRef(null);

  // Poll the player's current time while solo play is active
  useEffect(() => {
    if (soloIsPlaying) {
      pollRef.current = setInterval(() => {
        const t = playerRefs?.current?.[slotIndex]?.getCurrentTime?.();
        if (t != null) setSoloCurrentTime(t);
      }, 200);
    } else {
      clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
  }, [soloIsPlaying, playerRefs, slotIndex]);

  // Clean up solo state when widget is closed
  const handleToggleWidget = () => {
    if (showGreenFlag) {
      setSoloIsPlaying(false);
      setSoloCurrentTime(null);
      onEndSolo?.();
    }
    setShowGreenFlag((v) => !v);
  };

  const handleSoloPlay = () => {
    onStartSolo?.();
    setSoloIsPlaying(true);
  };

  const handleSoloPause = () => {
    setSoloIsPlaying(false);
  };

  const handleSoloScrub = (seconds) => {
    playerRefs?.current?.[slotIndex]?.seekTo(seconds, 'seconds');
    setSoloCurrentTime(seconds);
  };

  const videoUrl = videoData?.streamUrl
    || videoData?.youtubeStreamUrl
    || videoData?.tracksideVideoUrl
    || videoData?.tempLocalVideoLink
    || null;

  const label = videoData
    ? [videoData.carNum && `Car #${videoData.carNum}`, videoData.cameraAngle, videoData.drivername]
        .filter(Boolean)
        .join(' · ')
    : 'No camera';

  // Solo play overrides the main isPlaying for this slot only
  const effectivePlaying = showGreenFlag ? soloIsPlaying : isPlaying;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        bgcolor: 'black',
        borderRadius: 1,
        overflow: 'hidden',
        cursor: 'default',
        '&:hover .promote-btn': { opacity: isPrimary ? 0 : 1 },
      }}
    >
      {/* Aspect ratio container */}
      <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
        {videoUrl ? (
          <>
            <ReactPlayer
              ref={playerRef}
              url={videoUrl}
              playing={effectivePlaying}
              playbackRate={playbackRate}
              width="100%"
              height="100%"
              style={{ position: 'absolute', top: 0, left: 0 }}
              controls={false}
              onDuration={(d) => setVideoDuration(d)}
              muted={!isPrimary && !soloIsPlaying}
              config={{
                file: {
                  forceHLS: videoUrl?.includes('.m3u8'),
                  hlsOptions: { enableWorker: true },
                },
                youtube: {
                  playerVars: { disablekb: 1, modestbranding: 1 },
                },
              }}
            />
            {/* Transparent pane — blocks YouTube/native controls */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                cursor: isPrimary ? 'default' : 'pointer',
              }}
              onClick={!isPrimary ? onPromote : undefined}
            />
          </>
        ) : (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="grey.600">
              No video available
            </Typography>
          </Box>
        )}

        {/* Green flag button — top-right corner */}
        {videoData && (
          <Tooltip title={showGreenFlag ? 'Hide green flag' : 'Set green flag time'} placement="left">
            <IconButton
              size="small"
              onClick={handleToggleWidget}
              sx={{
                position: 'absolute',
                top: 6,
                right: 6,
                zIndex: 3,
                p: 0.5,
                bgcolor: 'rgba(0,0,0,0.45)',
                color: needsGreenFlag ? 'warning.main' : 'grey.400',
                opacity: showGreenFlag ? 1 : 0.4,
                '&:hover': { opacity: 1, bgcolor: 'rgba(0,0,0,0.7)', color: 'success.light' },
                transition: 'opacity 0.15s, color 0.15s',
              }}
            >
              <FlagIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Promote overlay (secondary only) */}
        {!isPrimary && videoData && (
          <Box
            className="promote-btn"
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              bgcolor: 'rgba(0,0,0,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              transition: 'opacity 0.15s',
              pointerEvents: 'none',
            }}
          >
            <Tooltip title="Make primary">
              <IconButton sx={{ color: 'white' }}>
                <OpenWithIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Label bar */}
      <Box
        sx={{
          px: 1,
          py: 0.5,
          bgcolor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography variant="caption" color="white" noWrap>
          {label}
        </Typography>
      </Box>

      {/* GreenFlagWidget */}
      {(needsGreenFlag || showGreenFlag) && videoData && (
        <GreenFlagWidget
          videoData={videoData}
          currentTime={soloCurrentTime}
          duration={videoDuration}
          isPlaying={soloIsPlaying}
          onPlay={handleSoloPlay}
          onPause={handleSoloPause}
          onScrub={handleSoloScrub}
          onSave={(updated) => {
            onGreenFlagSave?.(updated);
            setSoloIsPlaying(false);
            setSoloCurrentTime(null);
            onEndSolo?.();
            setShowGreenFlag(false);
          }}
        />
      )}
    </Box>
  );
}

export default BroadcastVideoSlot;
